import { DEFAULT_PAGE_SIZE, Dataset, DtFilter, DtSort } from '@/components/data-table/helpers';
import { PolicyModel } from '../models/policy-model/policy.model';
import { JsonObject } from '../helpers';
import { flatten, orderBy, uniq } from 'lodash';
import {
  CreateResponse,
  DEFAULT_SERVICE_ERROR,
  DeleteResponse,
  FieldError,
  ListResponse,
  PatchResponse,
  SAFE_LIMIT,
} from '.';
import { ContextProviderMeta } from '../models/context-provider';
import { getProvidersByIds } from './context-provider.service';
import { PrivateModelModel } from '../models/private-model/private-model.model';
import { ContextProviderModel } from '../models/context-provider/context-provider.model';
import { getPrivateModelsByIds } from './private-model.service';
import { AgentMeta, AgentModel } from '../models/agent.model';
import { getAgentsByIds } from './agent.service';
import { create, deleteApi, get, patch } from './sp-api.service';
import { AxiosError } from 'axios';
import { PolicyType } from '../models/policy-model';

type ServiceFieldError = {
  field: string;
  rule: string;
};

export type PolicyMetaResult = [
  Map<string, ContextProviderMeta>,
  Map<string, PrivateModelModel>,
  Map<string, AgentMeta>,
];

export const getScopedPolicies = async (
  page = 0,
  pageSize = DEFAULT_PAGE_SIZE,
  dtSort?: DtSort,
  dtFilter?: DtFilter
): Promise<Dataset<PolicyModel>> => {
  const response = (await get('/policies', {
    page: 1,
    pageSize: SAFE_LIMIT,
  })) as ListResponse;

  if (!response) {
    return {
      page,
      pageSize,
      rows: [],
      total: 0,
    };
  }

  let rows: PolicyModel[] = response.data
    .map((data) => new PolicyModel(data))
    .filter(({ isDefault, isOverride }) => !isDefault && !isOverride);

  if (dtFilter) {
    const { values, keyword } = dtFilter;

    if (keyword) {
      const safeKeyword = String(keyword).toLowerCase();
      rows = rows.filter(({ name }) => name.toLowerCase().includes(safeKeyword));
    }

    values.forEach(({ columnName, value }) => {
      switch (columnName) {
        case 'groupIds':
          rows = rows.filter(({ groups }) =>
            groups?.some((groupId) => (value as string[]).includes(groupId))
          );
          break;
      }
    });
  }

  // Sort
  if (dtSort) {
    const { columnName, direction } = dtSort;
    rows = orderBy(rows, [columnName], [direction]);
  }

  const total = rows.length;

  // Paginate
  const startIndex = page * pageSize;
  rows = rows.slice(startIndex, startIndex + pageSize);

  return {
    page,
    pageSize,
    rows,
    total,
  };
};

export const getAllPolicies = async (): Promise<PolicyModel[]> => {
  const response = (await get('/policies', { page: 1, pageSize: SAFE_LIMIT })) as ListResponse;
  return response?.data ? response.data.map((data) => new PolicyModel(data)) : [];
};

export const getAllGroupPolicies = async (): Promise<PolicyModel[]> => {
  return getScopedPolicies(0, SAFE_LIMIT).then(({ rows }) => rows);
};

export const getPolicy = async (
  policyType: PolicyType,
  policyId?: string
): Promise<PolicyModel | null> => {
  if (policyType === 'scoped') {
    return policyId ? getPolicyById(policyId) : null;
  }

  // @todo update to target with filter, once the filter is available
  const allPolicies = await getAllPolicies();

  return (
    allPolicies.find(({ isDefault, isOverride }) =>
      policyType === 'default' ? isDefault : isOverride
    ) || null
  );
};

export const getScopedPolicyWithParents = async (
  policyId: string
): Promise<[PolicyModel | null, PolicyModel, PolicyModel]> => {
  const allPolicies = await getAllPolicies();
  const policy = allPolicies.find(({ id }) => id === policyId);
  const defaultPolicy = allPolicies.find(({ isDefault }) => isDefault);
  const overridePolicy = allPolicies.find(({ isOverride }) => isOverride);
  return [policy || null, defaultPolicy || new PolicyModel(), overridePolicy || new PolicyModel()];
};

export const getPolicyById = async (policyId: string): Promise<PolicyModel | null> => {
  const response = await get(`/policies/${policyId}`);
  return response ? new PolicyModel(response as JsonObject) : null;
};

export const getPoliciesByIds = async (policyIds: string[]): Promise<PolicyModel[]> => {
  return getAllPolicies().then((policies) => policies.filter(({ id }) => policyIds.includes(id)));
};

export const getPoliciesByGroupIds = async (groupIds: string[]): Promise<PolicyModel[]> => {
  return getScopedPolicies(0, SAFE_LIMIT, undefined, {
    keyword: '',
    fields: [],
    values: [{ columnName: 'groupIds', value: groupIds }],
  }).then(({ rows }) => rows);
};

export const getOrgPolicy = async (): Promise<PolicyModel | null> => {
  const response = (await get('/policies', { page: 1, pageSize: 1, scope: 'org' })) as ListResponse;
  return response?.data?.length === 1 ? new PolicyModel(response.data[0]) : null;
};

export const getPoliciesByModelIds = async (modelIds: string[]): Promise<PolicyModel[]> => {
  const policies = await getAllPolicies();

  return policies.filter(
    ({ portal }) =>
      portal?.availablePrivateModelIds?.some((id) => modelIds.includes(id)) ||
      modelIds.includes(portal?.defaultModelId || '')
  );
};

export const getPoliciesByDatasourceIds = async (
  datasourceIds: string[]
): Promise<PolicyModel[]> => {
  const policies = await getAllPolicies();

  return policies.filter(({ contextDataSources }) =>
    contextDataSources?.access?.some(
      ({ dataSourceId, action }) => action === 'allow' && datasourceIds.includes(dataSourceId)
    )
  );
};

export const getPoliciesByAgentIds = async (agentIds: string[]): Promise<PolicyModel[]> => {
  const policies = await getAllPolicies();

  return policies.filter(({ assistants }) =>
    assistants?.access?.some(
      ({ assistantId, action }) => action === 'allow' && agentIds.includes(assistantId)
    )
  );
};

export const getPoliciesByServiceId = async (serviceId: string): Promise<PolicyModel[]> => {
  const policies = await getAllPolicies();
  return policies.filter(({ publicServices }) => {
    const { serviceAccess = [] } = publicServices || {};
    return serviceAccess.some(
      ({ serviceId: entityServiceId, action }) =>
        ['allowed', 'monitored'].includes(action) && entityServiceId === serviceId
    );
  });
};

/*
 * Note that this function does not have the same validation found in patchPolicy because the only
 * way to create a scoped policy at the moment is to first define just the name of the policy, save
 * it with all default settings, and then patch it afterwards
 */
// @todo UPDATE for scoped policies
export const createScopedPolicy = async (policy: PolicyModel): Promise<CreateResponse> => {
  const createData = {
    ...policy._props,
    description: null, // @todo can be removed once the policy service validation for this field is updated
    id: undefined,
    _lastChangedAt: undefined,
  };

  const response = await create('/policies', createData);

  if (!response.created) {
    return response;
  }

  response.model = new PolicyModel(response.data as JsonObject);

  return response;
};

export const patchPolicy = async (policyId: string, data: JsonObject): Promise<PatchResponse> => {
  return patch(`/policies/${policyId}`, data, handlePatchErrorResponse);
};

export const deletePolicy = async (policyId: string): Promise<DeleteResponse> => {
  return deleteApi(`/policies/${policyId}`);
};

export const getPolicyResources = async (policies: PolicyModel[]): Promise<PolicyMetaResult> => {
  const datasourceIds = flatten(
    policies.map(({ contextDataSources }) =>
      (contextDataSources?.access || [])
        ?.filter(({ action }) => action === 'allow')
        ?.map(({ dataSourceId }) => dataSourceId)
    )
  );

  const privateModelIds = flatten(
    policies.map(({ portal }) => portal?.availablePrivateModelIds || [])
  );

  const agentIds = uniq(
    flatten(
      policies.map(
        ({ assistants }) =>
          (assistants?.access || [])
            .filter(({ action }) => action === 'allow')
            ?.map(({ assistantId }) => assistantId) || []
      )
    )
  );

  if (!datasourceIds.length && !privateModelIds.length && !agentIds.length) {
    return [new Map(), new Map(), new Map()];
  }

  const [providers, models, agents] = await Promise.all([
    datasourceIds.length ? getProvidersByIds(datasourceIds) : [],
    privateModelIds.length ? getPrivateModelsByIds(privateModelIds) : [],
    agentIds.length ? getAgentsByIds(agentIds) : [],
  ]);

  const contextMap: Map<string, ContextProviderMeta> = new Map();

  (providers as ContextProviderModel[]).forEach((provider) => {
    contextMap.set(provider.id, provider.meta);
  });

  const modelMap: Map<string, PrivateModelModel> = new Map();

  (models as PrivateModelModel[]).forEach((model) => {
    modelMap.set(model.id, model);
  });

  const agentMap: Map<string, AgentMeta> = new Map();

  (agents as AgentModel[]).forEach((agents) => {
    agentMap.set(agents.id, agents.meta);
  });

  return [contextMap, modelMap, agentMap];
};

const handlePatchErrorResponse = (error: AxiosError): PatchResponse => {
  if (!error.response) {
    return { patched: false, error: DEFAULT_SERVICE_ERROR };
  }

  const { status, data } = error.response as { status: number; data: JsonObject };

  if (status === 400 && Array.isArray(data.errors)) {
    return { patched: false, fieldErrors: getFieldErrors(data.errors as ServiceFieldError[]) };
  }

  return { patched: false, error: DEFAULT_SERVICE_ERROR };
};

const getFieldErrors = (errors: ServiceFieldError[]): FieldError[] => {
  return errors.map(({ field, rule }) => ({
    type: 'validation',
    value: null,
    msg: rule,
    path: field,
    location: 'body',
  }));
};
